// Shape rules for student accounts (docs/classroom-design.md, "Student
// accounts and QR card sign-in"), shared by the Worker, the teacher
// dashboard and the game's "Scan your card" screen — one definition, so
// the three can't disagree about what a card code or a picture is.
//
// Secrets are hash-only (decision 2026-09-25): a card code and its
// pictures are shown once, when printed. Reprinting means a new card —
// new code AND new pictures — so the old card stops working.

import { RECOVERY_ALPHABET } from "./authRules.js";

// ---- card code --------------------------------------------------------------
// 12 Crockford base-32 characters ≈ 60 random bits: unguessable against a
// rate-limited endpoint, short enough to type from a printed card.

export const CARD_CODE_ALPHABET = RECOVERY_ALPHABET; // Crockford: no I, L, O, U
export const CARD_CODE_LENGTH = 12;

/** Upper-case, drop spaces/dashes, and map the look-alikes a child or an
 * adult might type (Crockford): I/L → 1, O → 0. */
export function normalizeCardCode(raw) {
  return String(raw ?? "")
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/[IL]/g, "1")
    .replace(/O/g, "0");
}

/** Display form: XXXX-XXXX-XXXX */
export function formatCardCode(raw) {
  return normalizeCardCode(raw).replace(/(.{4})(?=.)/g, "$1-");
}

export function isValidCardCodeShape(raw) {
  const bare = normalizeCardCode(raw);
  return bare.length === CARD_CODE_LENGTH && [...bare].every((c) => CARD_CODE_ALPHABET.includes(c));
}

/** The QR on a login card opens this. The code sits after `#`, so the
 * browser never sends it to the server; the game clears it from the
 * address bar straight away. */
export function cardLoginUrl(origin, code) {
  return `${origin}/#card=${normalizeCardCode(code)}`;
}

/** Pulls a card code out of a location hash like "#card=XXXX…", or null. */
export function cardCodeFromHash(hash) {
  const m = /(?:^#|&)card=([^&]+)/.exec(String(hash ?? ""));
  if (!m) return null;
  const code = normalizeCardCode(decodeURIComponent(m[1]));
  return isValidCardCodeShape(code) ? code : null;
}

// ---- picture password -------------------------------------------------------
// 3 pictures, in order, each any of 9 (repeats allowed): 9³ = 729. Weak on
// its own; it's only the second step after the card.

export const PICTURES = [
  { id: "fox", emoji: "🦊", label: "Fox" },
  { id: "owl", emoji: "🦉", label: "Owl" },
  { id: "frog", emoji: "🐸", label: "Frog" },
  { id: "bee", emoji: "🐝", label: "Bee" },
  { id: "fish", emoji: "🐟", label: "Fish" },
  { id: "tree", emoji: "🌳", label: "Tree" },
  { id: "flower", emoji: "🌻", label: "Sunflower" },
  { id: "mushroom", emoji: "🍄", label: "Mushroom" },
  { id: "leaf", emoji: "🍁", label: "Leaf" }
];
export const PICTURE_IDS = PICTURES.map((p) => p.id);
export const PICTURE_PASSWORD_LENGTH = 3;

export function isValidPicturePassword(pictures) {
  return (
    Array.isArray(pictures) &&
    pictures.length === PICTURE_PASSWORD_LENGTH &&
    pictures.every((p) => PICTURE_IDS.includes(p))
  );
}

export function pictureById(id) {
  return PICTURES.find((p) => p.id === id) ?? null;
}

// ---- avatars and names ------------------------------------------------------

export const AVATARS = [
  { id: "hedgehog", emoji: "🦔", label: "Hedgehog" },
  { id: "rabbit", emoji: "🐰", label: "Rabbit" },
  { id: "badger", emoji: "🦡", label: "Badger" },
  { id: "turtle", emoji: "🐢", label: "Turtle" },
  { id: "butterfly", emoji: "🦋", label: "Butterfly" },
  { id: "ladybird", emoji: "🐞", label: "Ladybird" },
  { id: "squirrel", emoji: "🐿️", label: "Squirrel" },
  { id: "duck", emoji: "🦆", label: "Duck" },
  { id: "snail", emoji: "🐌", label: "Snail" },
  { id: "deer", emoji: "🦌", label: "Deer" },
  { id: "otter", emoji: "🦦", label: "Otter" },
  { id: "bird", emoji: "🐦", label: "Bird" }
];
export const AVATAR_IDS = AVATARS.map((a) => a.id);

export function avatarById(id) {
  return AVATARS.find((a) => a.id === id) ?? null;
}

export const DISPLAY_NAME_MAX = 20;

/** A first name (or whatever the teacher chooses): 1–20 characters after
 * trimming, no control characters. The design keeps it to a first name,
 * no surname — that's guidance for the teacher, not something to police. */
export function validateDisplayName(raw) {
  const name = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (!name) return { ok: false, error: "Every student needs a name." };
  if ([...name].length > DISPLAY_NAME_MAX) return { ok: false, error: `Names can be up to ${DISPLAY_NAME_MAX} characters.` };
  if (/[\u0000-\u001f\u007f]/.test(name)) return { ok: false, error: "Names can't contain special control characters." };
  return { ok: true, value: name };
}

/** Synthetic email for a student's auth user — they never see or use it;
 * it only exists because Supabase Auth needs one. */
export const STUDENT_EMAIL_DOMAIN = "students.habitathero.app";
